import { Controller } from "@hotwired/stimulus"
import { openFilePickerAfterKeyboard } from "../helpers/file_picker"

export default class extends Controller {
  static targets = ["input", "sendButton", "attachments", "attachmentPreview"]
  static values = { conversationId: Number }

  connect() {
    this.autoResize()
    this.updateSendButton()
    this._bypassIntercept = false

    // Prevent send/attach buttons from blurring the textarea on iOS.
    // Without this, tapping a button dismisses the keyboard and shifts
    // the layout before the click registers, so the tap misses.
    this._preventBlur = (e) => {
      if (e.target !== this.inputTarget && !e.target.closest("textarea")) {
        e.preventDefault()
      }
    }
    this.element.addEventListener("mousedown", this._preventBlur)
  }

  disconnect() {
    this.element.removeEventListener("mousedown", this._preventBlur)
  }

  autoResize() {
    const textarea = this.inputTarget
    textarea.style.height = "auto"
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px"
    this.updateSendButton()
  }

  // Intercept form submission to check if referral mentions need sharing
  async interceptSubmit(event) {
    // Never submit while direct uploads are still in flight — the hidden
    // signed_id fields aren't populated until each upload completes.
    if (this.hasPendingUploads()) {
      event.preventDefault()
      return
    }

    if (this._bypassIntercept) {
      this._bypassIntercept = false
      return
    }

    const content = this.inputTarget.value
    // Quick client-side check — no #referral mentions means no need for AJAX
    if (!content.match(/#[^\s#][^#]*?\(\d{1,2}\/\d{1,2}\/\d{4}\)/)) {
      return
    }

    event.preventDefault()
    this.submitStart()

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]').content
      const response = await fetch(
        `/conversations/${this.conversationIdValue}/messages/check_sharing`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
          body: JSON.stringify({ content })
        }
      )
      const data = await response.json()

      if (data.needs_sharing) {
        this._restoreSendButton()
        this._showShareModal(data.modal_html)
      } else {
        this._bypassIntercept = true
        this.element.requestSubmit()
      }
    } catch (e) {
      // On error, submit normally (fail open)
      this._bypassIntercept = true
      this.element.requestSubmit()
    }
  }

  submitStart() {
    this.sending = true
    this.sendButtonTarget.disabled = true
    this.sendButtonTarget.innerHTML = '<span class="spinner-border" role="status" style="width:14px;height:14px;min-width:14px;min-height:14px;border-width:2px;flex-shrink:0"></span>'
  }

  handleKeydown(event) {
    // Submit on Enter (without Shift), but not while selecting a mention
    if (event.key === "Enter" && !event.shiftKey) {
      const mentionPopup = document.querySelector('.mention-popup:not(.d-none)')
      if (mentionPopup) return // Let mention controller handle this Enter

      event.preventDefault()
      if (!this.sending && !this.hasPendingUploads() && (this.inputTarget.value.trim() || this.hasAttachments())) {
        this.element.requestSubmit()
      }
    }
  }

  // Attach button (touchend on iOS, click elsewhere). Opens the picker at
  // once; see helpers/file_picker for why not after dropping the keyboard.
  openFilePicker(event) {
    if (event) event.preventDefault()
    openFilePickerAfterKeyboard(this.attachmentsTarget)
  }

  // Attachments are direct-uploaded to S3 by the file-upload controller on the
  // same form. It renders a .file-wrapper progress row per file and fills a
  // hidden file_upload[<key>][file] field with the blob signed_id on completion.
  hasAttachments() {
    return this.element.querySelectorAll(".file-wrapper--completed").length > 0
  }

  hasPendingUploads() {
    return this.element.querySelector(".file-wrapper--uploading") !== null
  }

  updateSendButton() {
    const hasContent = this.inputTarget.value.trim().length > 0
    const hasAttachments = this.hasAttachments()
    const canSend = hasContent || hasAttachments

    if (canSend) {
      this.sendButtonTarget.classList.add("btn-send-active")
    } else {
      this.sendButtonTarget.classList.remove("btn-send-active")
    }
  }

  reset() {
    this.sending = false
    this.sendButtonTarget.disabled = false
    this.sendButtonTarget.innerHTML = '<i class="fa-solid fa-paper-plane-top"></i>'
    this.inputTarget.value = ""
    this.inputTarget.style.height = "auto"
    this.attachmentsTarget.value = ""
    // Clears the file-upload controller's .file-wrapper rows and their hidden
    // signed_id/name fields.
    this.attachmentPreviewTarget.innerHTML = ""
    this._removeShareHiddenFields()
    this.updateSendButton()

    // Scroll to bottom of messages
    const messagesContainer = document.getElementById("messages-container")
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight
    }

    this.inputTarget.focus()
  }

  // --- Share modal helpers ---

  _showShareModal(html) {
    document.body.insertAdjacentHTML('beforeend', html)
    const modalEl = document.getElementById('modal_share_referral_prompt')

    // Wire up button handlers (modal is outside Stimulus controller scope)
    const shareBtn = modalEl.querySelector('[data-share-data]')
    const skipBtn = modalEl.querySelector('[data-skip-share]')

    if (shareBtn) {
      shareBtn.addEventListener('click', () => {
        const shareData = JSON.parse(shareBtn.dataset.shareData)
        this._appendShareFields(shareData)
        $(modalEl).modal('hide')
        this._bypassIntercept = true
        this.element.requestSubmit()
      })
    }

    if (skipBtn) {
      skipBtn.addEventListener('click', () => {
        $(modalEl).modal('hide')
      })
    }

    // Clean up modal element when hidden (X button, backdrop, or Not now)
    $(modalEl).on('hidden.bs.modal', () => {
      modalEl.remove()
      this._restoreSendButton()
    })

    $(modalEl).modal('show')
  }

  _appendShareFields(shareData) {
    Object.entries(shareData.referrals).forEach(([idx, refData]) => {
      this._addHidden(`share_referrals[${idx}][id]`, refData.id)
      refData.receiver_ids.forEach(rid => {
        this._addHidden(`share_referrals[${idx}][receiver_ids][]`, rid)
      })
    })
  }

  _addHidden(name, value) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    input.classList.add('share-referral-field')
    this.element.appendChild(input)
  }

  _removeShareHiddenFields() {
    this.element.querySelectorAll('.share-referral-field').forEach(el => el.remove())
  }

  _restoreSendButton() {
    this.sending = false
    this.sendButtonTarget.disabled = false
    this.sendButtonTarget.innerHTML = '<i class="fa-solid fa-paper-plane-top"></i>'
  }
}
